import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../ui/styles/classNames";
import { useToolCallDisplay, type ToolCallDisplay } from "../preferences/toolCallDisplay";
import { entryAnchor } from "./entryAnchors";
import { toolCallGroupStats, type ToolCallsBlock } from "./logBlocks";
import ToolCallDetails from "./ToolCallDetails";
import { callLine } from "./toolCallLines";
import { formatDuration } from "./toolValues";
import AppButton from "../../../ui/buttons/AppButton";

interface ToolCallGroupViewProps {
  block: ToolCallsBlock;
  threadId: string;
  highlightedEntry: number | null;
}

/**
 * One turn's tool calls as one block (web-app.md WA-4): "Worked 2 min · 23 tool calls",
 * opened to one line per call, each opened to its exact input and output. Hidden shows
 * only the count; Detailed opens everything. The agent's answer stays outside the block.
 */
export default function ToolCallGroupView(props: ToolCallGroupViewProps) {
  const display = useToolCallDisplay();
  // A new mode starts from its own defaults; within a mode the reader's toggles hold.
  return <GroupContents key={display} {...props} display={display} />;
}

function GroupContents({ block, highlightedEntry, display }: ToolCallGroupViewProps & { display: ToolCallDisplay }) {
  const { t, i18n } = useTranslation();
  const listId = useId();
  // A link to one of the calls opens the block, so the call can be scrolled to.
  const highlighted = block.calls.some((call) => call.number === highlightedEntry);
  const [hiddenOpen, setHiddenOpen] = useState(highlighted);
  const stats = toolCallGroupStats(block.calls);
  const list = (
    <ul id={listId} className="mt-1 space-y-1 pl-3">
      {block.calls.map((entry) => {
        const failed = entry.payload.outcome !== "ok";
        return (
          <li key={entry.number} id={entryAnchor(entry.number)} className={classNames("scroll-mt-4", entry.number === highlightedEntry && "app-warning-highlight")}>
            <details open={display === "detailed" || entry.number === highlightedEntry}>
              <summary className="cursor-pointer">
                <span className="inline-flex w-[calc(100%_-_1.25rem)] items-baseline gap-2 align-top">
                  <span role="img" aria-label={t(failed ? "threads.toolCall.failureStatus" : "threads.toolCall.ok")} className={failed ? "app-status-text-error" : "app-text-muted"}>
                    {failed ? "!" : "✓"}
                  </span>
                  <span className={classNames("min-w-0 flex-1 break-words", failed ? "app-status-text-error" : "app-text")}>{callLine(entry.payload, i18n.resolvedLanguage, t)}</span>
                  <span className="app-text-muted shrink-0 text-right tabular-nums">{formatDuration(entry.payload.duration_milliseconds, i18n.resolvedLanguage)}</span>
                </span>
              </summary>
              <ToolCallDetails payload={entry.payload} threadId={entry.thread_id} />
            </details>
          </li>
        );
      })}
    </ul>
  );
  return (
    <li className="app-text-muted px-2 py-1 text-xs">
      {display === "hidden" ? (
        <>
          <AppButton variant="ghost" size="xs" aria-expanded={hiddenOpen} aria-controls={listId} onClick={() => { setHiddenOpen(!hiddenOpen); }}>
            {t("threads.toolCalls.count", { count: stats.count })}
          </AppButton>
          <div hidden={!hiddenOpen}>{list}</div>
        </>
      ) : (
        <details open={display === "detailed" || highlighted}>
          <summary className="cursor-pointer">
            {t("threads.toolCalls.worked", { duration: formatDuration(stats.milliseconds, i18n.resolvedLanguage), count: stats.count })}
            {stats.failed === 0 ? null : <span className="app-status-text-error">{" · "}{t("threads.toolCalls.failed", { count: stats.failed })}</span>}
          </summary>
          {list}
        </details>
      )}
    </li>
  );
}
